import { images } from '../data/content'
import { upsell } from '../data/upsell'

/**
 * Image loading strategy.
 *
 * The hero <img> already carries fetchpriority="high", so the browser starts it
 * as soon as React mounts — nothing to add there.
 *
 * The upsell image lives inside a modal that only mounts on click, so the
 * browser would otherwise discover it far too late. We warm it here, at high
 * priority, using the exact same URL the <img> renders, so the modal opens
 * against a cached file.
 */
declare global {
  interface Window {
    __imagesPreloaded?: boolean
  }
}

type PriorityImage = HTMLImageElement & { fetchPriority?: string }

export function preloadImages() {
  if (typeof window === 'undefined' || window.__imagesPreloaded) return
  window.__imagesPreloaded = true

  let upsellWarmed = false
  const warmUpsell = () => {
    if (upsellWarmed) return
    upsellWarmed = true
    const img = new Image() as PriorityImage
    img.decoding = 'async'
    img.fetchPriority = 'high'
    img.src = upsell.image
  }

  // Give the hero the first slice of bandwidth, then grab the upsell straight
  // away — no idle callback, so it never waits for a quiet moment that may not
  // come before the visitor reaches the buy button.
  const hero = new Image()
  hero.src = images.heroBox

  if (hero.complete) {
    warmUpsell()
    return
  }

  hero.addEventListener('load', warmUpsell, { once: true })
  hero.addEventListener('error', warmUpsell, { once: true })
  // Safety net in case the hero stalls: never delay the upsell more than 1.5s.
  window.setTimeout(warmUpsell, 1500)
}
